import React from 'react';
import { Box, Grid, Link, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import AppsIcon from '@mui/icons-material/Apps';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import { useParams } from 'react-router-dom';
import PageHeader from '../components/shared/page-header';
import { useMainMenuStructure } from '../components/layout/app-menu';
import { useSystemLinks } from '../components/layout/system-links';
import { useMenusData } from '../../hooks/useMenusData';
import { useMenuLinkNavigation } from '../../hooks/useMenuLinkNavigation';
import { staticCardSx } from '../../theme/tokens';

interface SiteMapLink {
  label: string;
  path: string;
  isExternal?: boolean;
}

interface SiteMapGroup {
  title: string;
  icon: React.ReactElement;
  links: SiteMapLink[];
}

const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);
// Link externo cadastrado sem protocolo (ex.: "www.site.com.br"); rotas do portal nunca têm ponto
const isBareDomain = (url: string) => !isAbsoluteUrl(url) && /^[\w-]+(\.[\w-]+)+(\/|$)/i.test(url);
const opensNewTab = (link: SiteMapLink) => !!link.isExternal || isAbsoluteUrl(link.path) || isBareDomain(link.path);

/**
 * Mapa do site: todas as páginas do portal agrupadas como no menu (barra principal, "Mais opções"
 * e serviços externos). Os links são âncoras reais, navegáveis por teclado e indexáveis.
 */
export default function SiteMapComponent() {
  const mainStructure = useMainMenuStructure();
  const { menus } = useMenusData();
  const systemLinks = useSystemLinks();
  const openMenuLink = useMenuLinkNavigation();
  const params = useParams<{ state?: string; city?: string }>();

  const hrefOf = (path: string) => {
    if (isAbsoluteUrl(path)) return path;
    if (isBareDomain(path)) return `https://${path}`;
    const clean = path.replace(/^\//, '');
    return params.state && params.city ? `/${params.state}/${params.city}/${clean}` : `/${clean}`;
  };

  const handleClick = (link: SiteMapLink) => (e: React.MouseEvent) => {
    // Externos seguem o target do link; com Ctrl/Cmd o navegador abre a rota em nova aba
    if (opensNewTab(link) || e.ctrlKey || e.metaKey || e.shiftKey) return;
    e.preventDefault();
    openMenuLink(link.path);
  };

  // Barra principal: menus de link direto juntos em "Páginas principais"; os de dropdown em grupos próprios
  const mainPages = mainStructure.filter((item) => !item.subItems && item.path);
  const mainGroups: SiteMapGroup[] = [
    {
      title: 'Páginas principais',
      icon: <LinkIcon />,
      links: mainPages.map((item) => ({ label: item.label, path: item.path!, isExternal: item.isExternal })),
    },
    ...mainStructure
      .filter((item) => item.subItems?.length)
      .map((item) => ({
        title: item.label,
        icon: item.icon,
        links: item.subItems!.map((sub) => ({ label: sub.label, path: sub.path, isExternal: sub.isExternal })),
      })),
  ].filter((group) => group.links.length > 0);

  // "Mais opções": menus da entidade; os de link direto juntos em "Outras páginas"
  const moreGroups: SiteMapGroup[] = [
    ...menus
      .filter((menu) => !menu.url && menu.items?.length)
      .map((menu) => ({
        title: menu.name || '',
        icon: <AppsIcon />,
        links: menu.items!
          .filter((item) => !!item.url)
          .map((item) => ({ label: item.name || '', path: item.url!, isExternal: item.isExternal })),
      })),
    {
      title: 'Outras páginas',
      icon: <LinkIcon />,
      links: menus
        .filter((menu) => !!menu.url)
        .map((menu) => ({ label: menu.name || '', path: menu.url!, isExternal: menu.isExternal })),
    },
  ].filter((group) => group.links.length > 0);

  const serviceGroups: SiteMapGroup[] = [
    {
      title: 'Sistemas GovHub',
      icon: <OpenInNewIcon />,
      links: systemLinks.map((link) => ({
        label: link.label,
        path: link.href,
        isExternal: true,
      })),
    },
    {
      title: 'Acessibilidade e busca',
      icon: <AccessibilityNewIcon />,
      links: [
        { label: 'Acessibilidade', path: '/acessibilidade' },
        { label: 'Busca no portal', path: '/busca' },
      ],
    },
  ];

  const sections = [
    { id: 'mapa-navegacao-principal', title: 'Navegação principal', groups: mainGroups },
    { id: 'mapa-mais-opcoes', title: 'Mais opções', groups: moreGroups },
    { id: 'mapa-servicos', title: 'Serviços e acessibilidade', groups: serviceGroups },
  ].filter((section) => section.groups.length > 0);

  return (
    <Box>
      <PageHeader
        title="Mapa do site"
        subtitle="Todas as páginas e serviços do portal, organizados como no menu de navegação."
        breadcrumbs={[{ label: 'Mapa do site' }]}
      />

      <Box component="nav" aria-label="Mapa do site" sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 4, md: 5 } }}>
        {sections.map((section) => (
          <Box component="section" key={section.id} aria-labelledby={section.id}>
            <Typography id={section.id} variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              {section.title}
            </Typography>

            <Grid container spacing={2}>
              {section.groups.map((group) => (
                <Grid item xs={12} sm={6} md={4} key={group.title}>
                  <SiteMapCard group={group} hrefOf={hrefOf} onLinkClick={handleClick} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

interface SiteMapCardProps {
  group: SiteMapGroup;
  hrefOf: (path: string) => string;
  onLinkClick: (link: SiteMapLink) => (e: React.MouseEvent) => void;
}

function SiteMapCard({ group, hrefOf, onLinkClick }: SiteMapCardProps) {
  return (
    <Box sx={{ ...(staticCardSx as object), height: '100%', p: 2.5 }}>
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          fontWeight: 600,
          color: 'brand.text',
          lineHeight: 1.3,
          '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
        }}
      >
        <Box component="span" aria-hidden sx={{ display: 'inline-flex' }}>{group.icon}</Box>
        {group.title}
      </Typography>

      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {group.links.map((link) => {
          const newTab = opensNewTab(link);
          return (
            <li key={`${link.label}-${link.path}`}>
              <Link
                href={hrefOf(link.path)}
                target={newTab ? '_blank' : undefined}
                rel={newTab ? 'noopener noreferrer' : undefined}
                onClick={onLinkClick(link)}
                underline="hover"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  minHeight: 32,
                  color: 'text.primary',
                  fontSize: '0.9375rem',
                  '&:hover': { color: 'brand.text' },
                }}
              >
                {link.label}
                {newTab && (
                  <>
                    <OpenInNewIcon aria-hidden sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                    <Box component="span" sx={visuallyHidden}> (abre em nova aba)</Box>
                  </>
                )}
              </Link>
            </li>
          );
        })}
      </Box>
    </Box>
  );
}
